<template>
    <div class="cart-container" id="cart">
        <transition name="quick-fade">
            <div class="cart-background" v-if="isOpen" @click.self="close"></div>
        </transition>
        <transition name="slide">
            <div :class="['cart', { 'hidden-cart': isLoginModalOpen }, { success: status === CheckoutStatus.SUCCESS }]" v-if="isOpen">
                <div class="image-background layer-bg">
                    <img src="/media/static/svg/cart_confirmation_bg.svg" alt="Cart Success Background" />
                </div>
                <div class="cart-header">
                    <div class="align-bottom cart-header-container grid-x align-justify">
                        <div class="cell shrink">
                            <p class="margin-0" @click="close"><span class="close-button" v-html="Icon.CROSS"></span></p>
                            <p class="bold margin-0 cart-header-title">Your order</p>
                        </div>
                        <div class="cell shrink" v-if="status === CheckoutStatus.FORM">
                            <a @click="goToFood" class="show-for-medium cart-header-link bold">ADD ITEMS</a>
                        </div>
                    </div>
                </div>
                <div class="cart-content">
                    <div class="align-middle grid-x grid-padding-x">
                        <div class="cell auto">
                            <p class="display-large margin-bottom-0" v-if="products.length">Your items</p>
                        </div>
                        <pickup-time-modal dusk="cart-pickup-time-modal" class="hide-for-medium pickup-time-container" :show-opener="false"></pickup-time-modal>
                    </div>
                    <div v-if="isDelivery && !minOrderAmountReached && products.length" class="alert-container">
                        <alert :type="AlertType.WARNING" title="Delivery Awaits!">
                            <p>Reach the ${{ minOrderAmount }} minimum to unlock our delivery options.</p>
                        </alert>
                    </div>
                    <div class="grid-x grid-padding-x">
                        <div class="cell" v-if="maxProductsReached">
                            <div class="warning-box grid-x">
                                <span class="warning-icon cell shrink" v-html="Icon.ERROR"></span>
                                <div class="cell auto">
                                    <p class="display-small-medium margin-0">Your basket is full</p>
                                    <p class="display-small margin-0">Sorry, you can only order {{ maxOrderProducts }} products at a time.</p>
                                </div>
                            </div>
                        </div>
                        <div class="cell" v-if="status === CheckoutStatus.FORM || status === CheckoutStatus.LOADING">
                            <div class="grid-x">
                                <div class="cell cart-products">
                                    <div class="text-center cart-centered" v-if="!products.length">
                                        <img class="no-orders-icon" src="/media/static/svg/no_orders.svg" alt="No orders icon" />
                                        <p class="color-battleship-gray margin-top-1 display-small">Your basket is empty :(</p>
                                        <button class="button ghost margin-top-2" @click="goToFood">Add some delicious food</button>
                                    </div>
                                    <cart-product v-for="product in products" :key="product.id" :product="product" @remove="removeProduct(product)" :disabled="status === CheckoutStatus.LOADING"></cart-product>
                                </div>
                                <div class="cell" v-if="products.length">
                                    <cart-order-options />
                                </div>
                                <div class="cell" v-if="user">
                                    <div class="cell cart-contact" v-if="products.length">
                                        <cart-contact-info dusk="cart-contact-info"></cart-contact-info>
                                    </div>
                                    <div class="cell" v-if="products.length">
                                        <cart-time-info />
                                    </div>
                                    <div class="cell cart-address" v-if="products.length">
                                        <cart-delivery-info dusk="cart-delivery-info" />
                                    </div>
                                    <div class="cell cart-tip" v-if="products.length">
                                        <!-- <alert :type="AlertType.INFO" title="COVID-19" style="margin-bottom: 25px" v-if="isDelivery">
                                            <p>If you are in quarantine, please <b>DO NOT</b> interact with the driver in any way.</p>
                                        </alert> -->
                                        <cart-tip dusk="cart-tip" />
                                    </div>
                                    <div class="cell cart-payment" v-if="products.length">
                                        <cart-payment-info></cart-payment-info>
                                    </div>
                                    <div class="cell cart-summary" v-if="products.length">
                                        <div class="flex-container align-justify">
                                            <p class="light">Subtotal</p>
                                            <p class="light" dusk="subtotal">{{ subtotal }}</p>
                                        </div>
                                        <div class="flex-container align-justify" v-if="isMembershipApplied">
                                            <p class="color-success bold">Discount (5%)</p>
                                            <p class="color-success bold">{{ discount }}</p>
                                        </div>
                                        <div class="flex-container align-justify" v-if="isDelivery">
                                            <p class="light">Delivery Fee</p>
                                            <p class="light" dusk="delivery-fee">KYD {{ deliveryFee }}</p>
                                        </div>
                                        <div class="flex-container align-justify" v-for="fee in extraFees" :key="fee.id">
                                            <p class="light">{{ fee.label }}</p>
                                            <p class="light" dusk="extra-fees">{{ extraFeeAmount(fee.amount) }}</p>
                                        </div>
                                        <div class="flex-container align-justify" v-if="tipAmount">
                                            <p class="light">Tip</p>
                                            <p class="light" dusk="tip-amount">{{ tipAmount }}</p>
                                        </div>
                                        <div class="flex-container align-justify">
                                            <p class="display-large">Total</p>
                                            <p class="display-large" dusk="total">CI {{ total }}</p>
                                        </div>
                                        <div class="flex-container align-justify" v-if="conversionRate">
                                            <p class="display-small color-battleship-gray">
                                                <i>USD {{ conversionRate }}</i>
                                            </p>
                                            <p class="display-small color-battleship-gray">
                                                <i>USD {{ totalInUSD }}</i>
                                            </p>
                                        </div>

                                    </div>

                                    <div v-if="products.length && checkoutErrors.length && showErrorAlert">
                                        <alert :type="AlertType.ERROR" title="Something went wrong">
                                            <ul>
                                                <li v-for="(checkoutError, i) in checkoutErrors" :key="i">{{ checkoutError.message }}</li>
                                            </ul>
                                        </alert>
                                    </div>
                                    <div class="text-center cell order-button" v-if="products.length">
                                        <button class="button expanded" dusk="place-order" :disabled="!canOrder || isLoading" @click="placeOrder">
                                            {{ status === CheckoutStatus.LOADING ? 'Placing your order...' : 'Place Order' }}
                                        </button>
                                        <p class="display-xsmall color-battleship-gray">
                                            By completing this order, I agree to all
                                            <router-link :to="{ name: 'terms' }" class="underline bold">Terms &amp; Conditions.</router-link>
                                        </p>
                                    </div>
                                </div>
                                <div class="text-center cell login-callout" v-else>
                                    <button class="button expanded" @click="openLoginModal" dusk="cart-signin-btn">Sign In to continue</button>
                                </div>
                            </div>
                        </div>
                        <div class="cell" v-if="status === CheckoutStatus.ERROR">
                            <div class="grid-x">
                                <div class="cell cart-error">
                                    <div class="text-center cart-centered">
                                        <p class="display-large-bold color-dark-gray">Something went wrong</p>
                                        <p>Sorry something went wrong when processing your order, please try again later or order directly at the counter.</p>
                                        <p v-if="checkoutError" class="text-center color-alert display-small" v-html="checkoutError"></p>
                                        <button class="button button-primary" @click="tryAgain">Try again</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="cell" v-if="status === CheckoutStatus.SUCCESS">
                            <div class="grid-x">
                                <div class="cell cart-success">
                                    <div class="text-center cart-centered" v-if="lastOrder">
                                        <div class="cart-product-image lazy" v-if="lastOrder.products[0].image" :style="{ backgroundImage: `url('${lastOrder.products[0].image.card}')` }"></div>
                                        <h2 class="cart-greeting">Thanks {{ userName }}</h2>
                                        <p class="display-large notification-text">
                                            Hang tight! You’ll receive a notification when your food is
                                            {{ lastOrder.is_picked_up ? 'ready to collect!' : "on it's way to you!" }}
                                        </p>
                                        <p class="display-small success-alt">Your order is scheduled for</p>
                                        <p class="semi-bold">
                                            {{ lastOrderScheduledTime }}
                                        </p>
                                        <p class="display-small success-alt margin-0">Order #{{ lastOrder.id }}</p>
                                        <button class="margin-top-1 button success-color ghost" @click="seeOrders">See my orders</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>
<script lang="ts">
import Component, { mixins } from 'vue-class-component';
import CartModule, { CheckoutStatus } from '@/stores/modules/cart';
import { getModule } from 'vuex-module-decorators';
import CartProduct from './CartProduct.vue';
import PickupTimeModal from '@/components/front/time-scheduler/PickupTimeModal.vue';
import Product from '@/models/product';
import UserModule from '@/stores/modules/user';
import CartContactInfo from './CartContactInfo.vue';
import CartPaymentInfo from './CartPaymentInfo.vue';
import CartDeliveryInfo from './CartDeliveryInfo.vue';
import CartTimeInfo from './CartTimeInfo.vue';
import CartTip from './CartTip.vue';
import CartOrderOptions from './CartOrderOptions.vue';
import Alert from '@/components/common/ui/Alert.vue';
import { AlertType } from '@/utils/types';
import TimeSchedulerModule, { OrderRetrievalMethod } from '@/stores/modules/time-scheduler';
import { Icon } from '@/front/icons';
import TimeSlot, { TimeSlotState } from '@/models/time-slot';
import Settings, { SettingNamespace } from '@/utils/settings';
import { isStoreOpen } from '@/utils/store-schedule';
import { numberToCurrency } from '@/utils/utilities';
import { pushGaEvent } from '@/utils/analytics';
import Availability from '@/front/mixins/availabitity';
import ProductType from '@/models/product-type';

@Component({
    components: {
        PickupTimeModal,
        CartProduct,
        CartContactInfo,
        CartPaymentInfo,
        CartDeliveryInfo,
        CartTimeInfo,
        Alert,
        CartTip,
        CartOrderOptions,
    },
})
export default class Cart extends mixins(Availability) {
    cartModule!: CartModule;
    userModule!: UserModule;
    timeSchedulerModule!: TimeSchedulerModule;
    lastOrderTimeSlot: TimeSlot | null = null;
    isLoading: boolean = false;
    conversionRate: string = '';
    minOrderAmount: number = 0;


    async created() {
        this.cartModule = getModule(CartModule, this.$store);
        this.userModule = getModule(UserModule, this.$store);
        this.timeSchedulerModule = getModule(TimeSchedulerModule, this.$store);
        this.cartModule.setCheckoutStatus(CheckoutStatus.FORM);
        const settings = await Settings.getInstance();
        this.minOrderAmount = settings.get(SettingNamespace.ORDERS, 'min_order_amount')!.value;
        const deliveryFee = settings.get(SettingNamespace.ORDERS, 'min_delivery_fee')!.value ? settings.get(SettingNamespace.ORDERS, 'min_delivery_fee')!.value : 0;
        this.cartModule.setDeliveryFee(parseFloat(deliveryFee));
        this.conversionRate = settings.get(SettingNamespace.PAYMENT, 'conversion_rate')!.value;
        const extraFees = settings.get(SettingNamespace.ORDERS, 'extra_fees') ? settings.get(SettingNamespace.ORDERS, 'extra_fees').value : [];
        this.cartModule.setExtraFees(extraFees);

        if (!(await isStoreOpen())) {
            this.close();
        }
    }

    mounted() {
        this.$watch('retrievalMethod', () => this.cartModule.clearAllCheckoutErrors());
    }

    close() {
        this.cartModule.setIsOpen(false);
    }

    goToFood() {
        this.close();
        if (this.$router.currentRoute.name !== 'food') {
            this.$router.push({ name: 'food' });
        }
    }

    removeProduct(product: Product) {
        this.cartModule.removeProduct(product);
        pushGaEvent({
            event: 'remove_from_cart',
            ecommerce: {
                currency: 'KYD',
                value: product.price,
                items: [
                    {
                        item_id: product.id!.toString(),
                        item_name: product.is_preset ? product.name : 'Custom - ' + product.name,
                        quantity: product.quantity!,
                        price: product.price,
                        item_category: product.type.name,
                    },
                ],
            },

        });
    }

    async placeOrder() {
        if (this.isLoading) {
            return false;
        }
        this.isLoading = true;
        if (this.userModule.draftUser && this.userModule.currentUser) {
            for (const field in this.checkoutFields) {
                this.cartModule.validateCheckoutField(field);
            }
            // check if product is still available
            for (const product of this.products) {
                let updatedProduct = await Product.find(product.origin ? product.origin.id! : product.id!);
                if (updatedProduct) {
                    if (updatedProduct.schedule && this.isProductUnavailable(updatedProduct)) {
                        this.cartModule.addCheckoutError({
                            source: 'product',
                            message: `${updatedProduct.name} is only ${this.scheduleText(updatedProduct.schedule).toLowerCase()}. Please update the scheduled time or remove this item from your cart.`,
                        });
                        break;
                    }
                    const productType = await ProductType.find(updatedProduct!.type.id!);
                    if (productType.product_pages) {
                        for (const productPage of productType.product_pages) {
                            if (productPage.schedule && this.isPageUnavailable(productPage)) {
                                this.cartModule.addCheckoutError({
                                    source: 'product',
                                    message: `${updatedProduct.name} is only ${this.scheduleText(productPage.schedule).toLowerCase()}. Please update the scheduled time or remove this item from your cart.`,
                                });
                                break;
                            }
                        }
                    }
                }
            }

            if (!this.canOrder) {
                this.cartModule.setShowErrorAlert(true);
                this.isLoading = false;
                return false;
            }

            if (this.cartModule.saveAddress) {
                this.userModule.draftUser.default_address = this.cartModule.checkoutFields.deliveryAddress.value;
                this.userModule.draftUser.address_place_id = this.cartModule.userAddressPlaceId;
                this.userModule.draftUser.business_name = this.cartModule.checkoutFields.businessName.value;
                this.userModule.draftUser.delivery_instructions = this.cartModule.checkoutFields.deliveryInstructions.value;
            }
            this.userModule.draftUser.name = this.cartModule.checkoutFields.name.value;
            this.userModule.draftUser.email = this.cartModule.checkoutFields.email.value;
            this.userModule.draftUser.phone = this.cartModule.checkoutFields.phone.value;
            this.userModule.draftUser.membership_number = this.cartModule.checkoutFields.membershipNumber.value;

            const updateSuccess = await this.userModule.saveDraft();
            if (!updateSuccess) {
                this.isLoading = false;
                return this.cartModule.setRegistrationError(true);
            }
            this.lastOrderTimeSlot = this.timeSchedulerModule.selectedTimeSlot;
            const order = await this.cartModule.placeOrder({
                user: this.userModule.currentUser,
                scheduled_time: this.timeSchedulerModule.selectedTimeSlot!.start(),
                is_picked_up: this.timeSchedulerModule.retrievalMethod === OrderRetrievalMethod.PICK_UP,
            });
            if (order) {
                this.isLoading = false;
                this.userModule.fetchUserActiveOrders();
            }
            this.isLoading = false;
        }
    }

    tryAgain() {
        this.cartModule.setCheckoutStatus(CheckoutStatus.FORM);
    }

    openLoginModal() {
        this.userModule.openLoginModal();
    }

    seeOrders() {
        this.cartModule.setIsOpen(false);
        this.$router.push({ name: 'orders' });
    }

    get subtotal() {
        return numberToCurrency(this.cartModule.subtotal);
    }

    get deliveryFee() {
        return this.isDelivery ? numberToCurrency(this.cartModule.deliveryFee) : '';
    }

    get total() {
        return numberToCurrency(this.cartModule.total);
    }

    get totalInUSD() {
        return numberToCurrency(this.cartModule.total / parseFloat(this.conversionRate));
    }

    get isDelivery() {
        return this.cartModule.isDelivery;
    }

    get tipAmount() {
        return this.cartModule.tipAmount ? numberToCurrency(this.cartModule.tipAmount) : '';
    }

    get minOrderAmountReached() {
        return !this.isDelivery ? true : this.products.length && this.cartModule.subtotal >= this.minOrderAmount;
    }

    get maxProductsReached() {
        return this.cartModule.maxProductsReached;
    }

    get maxOrderProducts() {
        return this.cartModule.maxOrderProducts;
    }

    get lastOrder() {
        return this.cartModule.lastOrder;
    }

    get discount() {
        return numberToCurrency(this.cartModule.subtotal * 0.05);
    }

    get extraFeeAmount() {
        return (feeAmount: number) => {
            return numberToCurrency(this.cartModule.subtotal * feeAmount / 100);
        }
    }

    get extraFees() {
        return this.cartModule.extraFees;
    }

    get lastOrderRetrievalMethod() {
        if (!this.lastOrder?.is_picked_up) return OrderRetrievalMethod.DELIVERY;
        return OrderRetrievalMethod.PICK_UP;
    }

    get isMembershipApplied() {
        return this.cartModule.isMembershipApplied;
    }

    get user() {
        return this.userModule.currentUser;
    }

    get userName() {
        if (this.user) {
            return this.user.name.split(' ')[0];
        }
        return '';
    }

    get isOpen() {
        return this.cartModule.isOpen;
    }

    get isLoginModalOpen() {
        return this.userModule.loginModalOpen;
    }

    get CheckoutStatus() {
        return CheckoutStatus;
    }

    get status() {
        return this.cartModule.status;
    }

    get products() {
        return Product.createBatch(this.cartModule.products);
    }

    get showErrorAlert() {
        return this.cartModule.showErrorAlert;
    }

    get checkoutFields() {
        return this.cartModule.checkoutFields;
    }

    get checkoutErrors() {
        return this.cartModule.checkoutErrors;
    }

    get errorMessage() {
        return (field: string) => {
            const error = this.checkoutErrors.find((error) => error.source === field);
            if (error) {
                return error.message;
            }
        };
    }

    get isRetrievalMethodOpen() {
        return this.timeSchedulerModule.isRetrievalMethodAvailable();
    }

    get slotsAvailable() {
        return this.timeSchedulerModule.timeSlots.filter((timeSlot: TimeSlot) => timeSlot.state === TimeSlotState.AVAILABLE).length > 0;
    }

    get canOrder() {
        return this.minOrderAmountReached && this.isRetrievalMethodOpen && this.slotsAvailable && (this.checkoutErrors.length === 0 || (this.checkoutErrors.length === 1 && this.checkoutErrors[0].source === 'payment') || (this.checkoutErrors.length === 1 && this.checkoutErrors[0].source === 'membership'));
    }

    get Icon() {
        return Icon;
    }

    get AlertType() {
        return AlertType;
    }

    get retrievalMethod() {
        return this.timeSchedulerModule.retrievalMethod;
    }

    get lastOrderScheduledTime() {
        if (this.lastOrderTimeSlot) {
            if (this.isDelivery) {
                const start = new Date(this.lastOrderTimeSlot.start().getTime() + this.timeSchedulerModule.deliveryTime * 60 * 1000);
                const end = new Date(this.lastOrderTimeSlot.end().getTime() + this.timeSchedulerModule.deliveryTime * 60 * 1000);
                return start.toLocaleString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true }) + ' - ' + end.toLocaleString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true });
            }
            return this.lastOrderTimeSlot.parsedStart + ' - ' + this.lastOrderTimeSlot.parsedEnd;
        }
        return '';
    }
}
</script>
<style lang="scss" scoped>
@import '~foundation-sites/scss/foundation';
@import '~scss/front/settings';

.alert-container {
    margin: 10px;
}

.cart-container {
    .close-button {
        margin-bottom: 10px;
        width: 25px;
        display: inline-block;
        cursor: pointer;

        path {
            fill: var(--dark-gray);
        }

        &:hover {
            circle {
                fill: var(--light-gray);
            }
        }
    }

    .cart {
        background: var(--cart-bg);
        height: 100vh;
        height: -webkit-fill-available;
        overflow-y: auto;
        position: fixed;
        top: 0;
        bottom: 0;
        right: 0;
        width: 525px;
        box-shadow: 5px 5px 40px 0 rgba(0, 0, 0, 0.09);
        z-index: 20;

        .cart-header {
            padding: 30px 30px 0px 30px;
            background-color: var(--cart-header-bg-color-desktop);

            @include breakpoint(small only) {
                padding: 30px 20px 0px 20px;
                background-color: var(--cart-header-bg-color-mobile);
            }

            .cart-header-container {
                padding-bottom: 20px;
                @include breakpoint(medium up) {
                    border-bottom: 1px solid var(--cart-separator-color);
                }
                .cart-header-title {
                    font-size: 25px;
                    line-height: 30px;
                }
                .cart-header-title,
                .cart-header-link {
                    color: var(--cart-header-fg-desktop);

                    @include breakpoint(small only) {
                        color: var(--cart-header-fg-mobile);
                    }
                }
            }
        }

        .image-background {
            display: none;
        }

        &.success {
            background: var(--cart-success-bg);
            .cart-header {
                background-color: var(--cart-success-header-bg-color-desktop);
            }
            .cart-header-container {
                @include breakpoint(medium up) {
                    border-bottom: 1px solid var(--cart-separator-success-color);
                }

                .cart-header-title,
                .cart-header-link {
                    color: var(--cart-header-success-fg-desktop);

                    @include breakpoint(small only) {
                        color: var(--cart-header-success-fg-mobile);
                    }
                }
            }

            .cart-content {
                color: var(--cart-success-fg);
            }

            .cart-success {
                color: var(--cart-success-fg);

                .success-alt {
                    color: var(--cart-success-alt-fg);
                }
            }

            .image-background {
                position: absolute;
                display: block;
                width: 100%;
                height: 100%;
                z-index: -1;
                overflow: hidden;
            }

            .button.success-color {
                background: var(--cart-success-btn-bg);
                color: var(--cart-success-btn-color);
                border-color: var(--cart-success-btn-border);

                &:hover {
                    background: var(--cart-success-btn-bg-hover);
                    color: var(--cart-success-btn-color-hover);
                    border-color: var(--cart-success-btn-border-hover);
                }
            }
        }

        .cart-content {
            padding: 30px 20px 45px 20px;
            @include breakpoint(small only) {
                padding: 30px 10px 100px 10px;
            }
        }

        &.hidden-cart {
            @include breakpoint(medium down) {
                z-index: 11;
            }
        }
        @include breakpoint(medium down) {
            width: 100%;
            left: 0;
            box-shadow: none;
        }

        .cart-time-info,
        .cart-address {
            padding-bottom: 30px;
        }

        .cart-contact-info {
            &:not(.open) {
                padding-bottom: 30px;
            }
        }

        .cart-tip {
            padding-bottom: 8px;
        }
        .cart-summary {
            border-bottom: var(--cart-separator);
            border-top: var(--cart-separator);
            border-color: var(--cart-separator-color);
            padding: 30px 0 30px 0;
            margin-bottom: 25px;

            p {
                margin-bottom: 9px;

                &:last-child {
                    margin-bottom: 0;
                }
            }
        }

        .cart-title {
            margin-bottom: 5px;
        }

        .cart-edit {
            text-transform: uppercase;
            color: var(--cart-edit-color);
            font-weight: 600;
            letter-spacing: 1px;
        }

        .cart-centered {
            padding: 50px 0 0;
        }

        .login-callout {
            padding-top: 30px;
        }

        .order-button {
            padding-top: 25px;
        }

        .pickup-time-container > .pickup-time-modal-container {
            padding: 0 !important;
        }

        .pickup-time-modal-button {
            @include breakpoint(small only) {
                position: absolute;
                top: 15px;
                right: 10px;
            }
        }

        .cart-greeting {
            padding-top: 21px;
            margin-bottom: 23px;
        }

        .cart-success {
            .cart-content {
                color: var(--cart-success-fg) !important;
            }
            .cart-product-image {
                border-radius: 50%;
                width: 150px;
                background-position: center;
                background-size: cover;
                height: 150px;
                margin: auto;
            }

            .notification-text {
                max-width: 330px;
                display: block;
                margin: auto;
                line-height: 25px;
                padding-bottom: 20px;
            }
        }
    }

    .cart-background {
        @include breakpoint(large) {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: 3;
            background: rgba(255, 255, 255, 0.5);
            backdrop-filter: blur(5px);
        }
    }
}
</style>
